// client/src/v2/components/drawer/SchoolDetailDrawer.jsx

import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useContext,
  useLayoutEffect,
} from "react";
import { usePressWithoutScroll } from "../usePressWithoutScroll";
import { useCatchmentForDrawer } from "../../domains/catchmentV2/useCatchmentForDrawer";
import { useAdmissionsPolicies } from "../../domains/admissions/useAdmissionsPolicies";
import { getOpenSeatsHeaderChipInfo } from "../../utils/catchmentOpenSeatsPreview";
import {
  pickLatestAdmissionPolicy,
  formatExamSubjectsChipFromPolicy,
} from "../../utils/examDetailsUtils";
import CatchmentPanel from "./CatchmentPanel";
import ExamDetailsPanel from "./ExamDetailsPanel";
import AllocationPanel from "./AllocationPanel";
import "./stats/drawerStats.css";
import { formatOversubscriptionFactor, oversubscriptionFromHistory } from "./oversubscription";
import GcseResultsPanel from "./GcseResultsPanel";
import AlevelResultsPanel from "./AlevelResultsPanel";
import Destinations1618Panel from "./Destinations1618Panel";
import SubjectsPanel from "./SubjectsPanel";
import InspectionsPanel from "./InspectionsPanel";
import { useGcseResults } from "../../domains/gcse/useGcseResults";
import { useKs5Results } from "../../domains/ks5/useKs5Results";
import { useInspections } from "../../domains/inspections/useInspections";
import { useSchoolSubjects } from "../../domains/schoolSubjects/useSchoolSubjects";
import { getInspectionOverallChip } from "../../utils/inspectionGradeUtils";
import { isKs5AcademicIbFallback } from "../../utils/ks5AcademicIb";
import { withStageRanks } from "../../ranking/rankSchools";
import { useGrammarBenchmarks } from "./stats/useGrammarBenchmarks";
import InfoTip from "../InfoTip";

const RESULTS_YEAR_TAB_ORDER = [2025, 2024, 2023];

function pickRowByYearTab(rows, order) {
  const byYear = new Map();
  for (const r of rows) {
    if (r.year_tab != null) byYear.set(Number(r.year_tab), r);
  }
  for (const y of order) {
    if (byYear.has(y)) return byYear.get(y);
  }
  return null;
}

/** GCSE grades 8–9 % chip: green from this %, yellow below. */
const GCSE_CHIP_GRADES_89_HIGH_PCT = 60;
/** GCSE grades 8–9 % chip: show ★ when strictly above this % (still green/yellow from high threshold above). */
const GCSE_CHIP_GRADES_89_STAR_PCT = 80;
/** A level % A* or A chip: green from this %, yellow below. */
const ALEVEL_CHIP_ASTAR_A_HIGH_PCT = 50;
/** A level % A* or A: show ★ outside the chip when strictly above this %. */
const ALEVEL_CHIP_ASTAR_A_STAR_PCT = 70;

function fmtResultsPctChip(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `${n.toFixed(1)}%`;
}

function resultsHeaderChipTier(rawPct, thresholdPct) {
  if (rawPct == null || rawPct === "") return null;
  const n = Number(rawPct);
  if (!Number.isFinite(n)) return null;
  return n >= thresholdPct ? "high" : "low";
}

function isEnabledFlag(school, flag) {
  // Core principle: if a flag is missing, behave as "enabled" (non-breaking).
  // Only hide a section when the flag exists AND is explicitly false.
  if (!school || !flag) return true;
  return school[flag] !== false;
}

// Monochrome SVG icons for drawer panels (Locrating-style)
const DRAWER_ICONS = {
  details: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  catchment: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  exam: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
  allocation: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  gcse: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  alevel: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M22 10v6" />
      <path d="M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" />
    </svg>
  ),
  oxbridge: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  ),
  inspection: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  subjects: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="16" y2="10" />
    </svg>
  ),
};

const EXAM_CHIP_FS_MAX = 12;
const EXAM_CHIP_FS_MIN = 8;

/** Single-line exam subjects chip: stays default size for short labels, scales down when needed. */
function ExamSubjectsHeaderChip({ label }) {
  const wrapRef = useRef(null);
  const chipRef = useRef(null);

  const fit = useCallback(() => {
    const wrap = wrapRef.current;
    const chip = chipRef.current;
    if (!wrap || !chip || !label) return;

    chip.style.fontSize = "";
    chip.style.padding = "";
    chip.style.paddingInline = "";
    chip.style.lineHeight = "";

    const avail = wrap.clientWidth;
    if (avail <= 1) return;

    let fs = EXAM_CHIP_FS_MAX;
    chip.style.fontSize = `${fs}px`;
    void chip.offsetHeight;

    while (fs > EXAM_CHIP_FS_MIN && chip.offsetWidth > avail) {
      fs -= 0.5;
      chip.style.fontSize = `${fs}px`;
      void chip.offsetHeight;
    }

    if (fs < EXAM_CHIP_FS_MAX) {
      const t = (EXAM_CHIP_FS_MAX - fs) / (EXAM_CHIP_FS_MAX - EXAM_CHIP_FS_MIN);
      const padY = Math.max(4, Math.round(5 - t * 1.5));
      chip.style.paddingBlock = `${padY}px`;
      chip.style.paddingInline = "12px";
      chip.style.lineHeight = "1.2";
    }
  }, [label]);

  useLayoutEffect(() => {
    fit();
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(fit);
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [fit]);

  return (
    <div className="v2-drawer-chip-exam-wrap" ref={wrapRef}>
      <span ref={chipRef} className="v2-drawer-chip v2-drawer-chip--exam-subjects">
        {label}
      </span>
    </div>
  );
}

/** Instant scroll only — `smooth` is unreliable inside iOS drawers / sheet stacks */
function scrollBlockIntoParent(container, blockEl, marginTopPx = 8) {
  if (!container || !blockEl || !blockEl.isConnected) return;
  const cRect = container.getBoundingClientRect();
  const bRect = blockEl.getBoundingClientRect();
  const nextTop = bRect.top - cRect.top + container.scrollTop - marginTopPx;
  const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
  container.scrollTop = Math.max(0, Math.min(nextTop, maxScroll));
}

/** Desktop: outer `.v2-drawer-sidebar` may clip; phone: `.v2-drawer-scroll` scrolls inside the sheet. */
function resolveDrawerScrollContainer(sectionEl, preferredRef) {
  const preferred = preferredRef?.current;
  const overflowYScrolls = (el) => {
    if (!el) return false;
    const oy = window.getComputedStyle(el).overflowY;
    return oy === "auto" || oy === "scroll" || oy === "overlay";
  };
  if (preferred && overflowYScrolls(preferred)) return preferred;
  const inner = sectionEl?.closest?.(".v2-drawer-scroll");
  if (inner && overflowYScrolls(inner)) return inner;
  const sidebar = sectionEl?.closest?.(".v2-drawer-sidebar");
  if (sidebar && overflowYScrolls(sidebar)) return sidebar;
  return preferred || inner || sidebar || null;
}

const SectionCapContext = React.createContext({
  cap: null,
  reportReference: () => {},
});

function Section({
  title,
  icon,
  children,
  defaultOpen = false,
  preview,
  previewClassName,
  onHeaderClick,
  /** School Details. Its open height is the size the other sections stop at. */
  reference = false,
  /** Phone sheet: when a row expands, scroll it into view so bodies (e.g. tables) aren’t clipped */
  scrollIntoViewOnExpand = false,
  /** Scrollable `.v2-drawer-scroll` on phone — manual scroll is reliable when header is outside scroll */
  scrollParentRef,
}) {
  const { cap, reportReference } = useContext(SectionCapContext);
  const [open, setOpen] = useState(!!defaultOpen);
  const rootRef = useRef(null);
  const bodyRef = useRef(null);
  const prevOpenRef = useRef(open);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (onHeaderClick) onHeaderClick(next);
      return next;
    });
  };
  const press = usePressWithoutScroll(toggle);
  const capped = !reference && cap != null;

  useLayoutEffect(() => {
    if (!reference) return undefined;
    const el = bodyRef.current;
    if (!el) return undefined;
    const report = () => {
      const height = el.scrollHeight;
      if (height > 40) reportReference(height);
    };
    report();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [reference, open, reportReference]);

  useLayoutEffect(() => {
    if (!scrollIntoViewOnExpand) {
      prevOpenRef.current = open;
      return;
    }
    prevOpenRef.current = open;
    if (!open) return;

    const sectionEl = rootRef.current;
    if (!sectionEl?.isConnected) return;

    const alignSectionToTop = () => {
      const sec = rootRef.current;
      if (!sec?.isConnected) return;
      const c = resolveDrawerScrollContainer(sec, scrollParentRef);
      const sidebar = sec.closest(".v2-drawer-sidebar");
      const desktopDrawer =
        Boolean(sidebar && !sidebar.classList.contains("v2-drawer-sidebar--phone"));
      /* Desktop: tighter top alignment + header spacing so the band under the school title doesn’t read as a double rule */
      const topMargin = desktopDrawer ? 0 : 4;
      if (c) scrollBlockIntoParent(c, sec, topMargin);
      else sec.scrollIntoView({ block: "start", behavior: "auto", inline: "nearest" });
    };

    alignSectionToTop();
    let alive = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (alive) alignSectionToTop();
      });
    });

    const bodyEl = bodyRef.current;
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        alignSectionToTop();
      });
      ro.observe(sectionEl);
      if (bodyEl) ro.observe(bodyEl);
    }

    const delays = [50, 120, 280, 500, 900, 1400];
    const timers = delays.map((ms) => window.setTimeout(alignSectionToTop, ms));

    return () => {
      alive = false;
      timers.forEach(clearTimeout);
      ro?.disconnect();
    };
  }, [open, scrollIntoViewOnExpand, scrollParentRef, title]);

  return (
    <div ref={rootRef} className="v2-drawer-section">
      <button
        type="button"
        className="v2-drawer-section-header"
        aria-expanded={open}
        onPointerDown={press.onPointerDown}
        onClick={press.onClick}
      >
        <div className="v2-drawer-section-header-left">
          {icon ? <span className="v2-drawer-section-icon">{icon}</span> : null}
          <span className="v2-drawer-section-title">{title}</span>
          {preview ? (
            <span
              className={["v2-drawer-section-preview", previewClassName].filter(Boolean).join(" ")}
            >
              {preview}
            </span>
          ) : null}
        </div>
        <span
          className={`v2-drawer-section-header-chevron${open ? " v2-drawer-section-header-chevron--open" : ""}`}
          aria-hidden="true"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </span>
      </button>

      {reference && !open ? (
        <div className="v2-drawer-section-measure" aria-hidden="true">
          <div ref={bodyRef} className="v2-drawer-section-body">
            {children}
          </div>
        </div>
      ) : null}
      {open ? (
        <div
          ref={bodyRef}
          className={`v2-drawer-section-body${capped ? " is-capped" : ""}`}
          style={capped ? { maxHeight: cap } : undefined}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export default function SchoolDetailDrawer({
  school,
  onClose,
  selectedIds = [],
  onShowCatchment,
  /** Opens the GCSE ranking page (Attainment 8). */
  onOpenRanking,
  /** Opens the School Guide page when a journey pack exists. */
  onOpenJourney,
  journeyAvailable = false,
  /** Phone bottom sheet: scroll detail body to top when opening / switching school */
  scrollToTopOnSchoolChange = false,
}) {
  const drawerScrollRef = useRef(null);

  useLayoutEffect(() => {
    if (school?.id == null) return;
    const inner = drawerScrollRef.current;
    const sidebar = inner?.closest?.(".v2-drawer-sidebar");
    const go = () => {
      if (inner) {
        const ioy = window.getComputedStyle(inner).overflowY;
        if (
          scrollToTopOnSchoolChange ||
          ioy === "auto" ||
          ioy === "scroll" ||
          ioy === "overlay"
        ) {
          inner.scrollTop = 0;
        }
      }
      if (sidebar && sidebar !== inner) {
        const oy = window.getComputedStyle(sidebar).overflowY;
        if (oy === "auto" || oy === "scroll" || oy === "overlay") sidebar.scrollTop = 0;
      }
    };
    go();
    requestAnimationFrame(go);
  }, [scrollToTopOnSchoolChange, school?.id]);
  const enabled = useMemo(() => {
    const admissionsOk = isEnabledFlag(school, "has_admissions");
    const hasCatchment = isEnabledFlag(school, "has_catchment");
    const isOpenCatchment = (school?.catchment_category || "").toLowerCase() === "open";
    return {
      catchment: (admissionsOk && hasCatchment) || isOpenCatchment,
      exams: admissionsOk && isEnabledFlag(school, "has_exams"),
      allocations: admissionsOk && isEnabledFlag(school, "has_allocations"),
      gcse: isEnabledFlag(school, "has_results_gcse"),
      alevel: isEnabledFlag(school, "has_results_alevel"),
      destinations:
        Boolean(school?.has_results_destinations) ||
        school?.oxbridge_offers != null ||
        Boolean(school?.source_url_oxbridge_offers),
      inspection: isEnabledFlag(school, "has_inspection"),
      subjects: isEnabledFlag(school, "has_subjects"),
    };
  }, [school]);

  const { payload, loading, error } = useCatchmentForDrawer(
    school?.id,
    enabled.catchment
  );

  const admissionsPoliciesEnabled = enabled.exams || enabled.allocations;
  const {
    policies: admissionsPolicies,
    allocationHistory: admissionsAllocationHistory,
    loading: admissionsLoading,
    error: admissionsError,
  } = useAdmissionsPolicies(school?.id, admissionsPoliciesEnabled);

  const catchmentOpenSeatsChip = useMemo(() => {
    if (!enabled.catchment || loading || error || !payload) return null;
    return getOpenSeatsHeaderChipInfo(payload, school);
  }, [enabled.catchment, loading, error, payload, school]);

  const examHeaderPreview = useMemo(() => {
    if (!enabled.exams || admissionsLoading || admissionsError) return null;
    const policy = pickLatestAdmissionPolicy(admissionsPolicies);
    const subjectsLabel = formatExamSubjectsChipFromPolicy(policy);
    if (!subjectsLabel) return null;
    return <ExamSubjectsHeaderChip label={subjectsLabel} />;
  }, [enabled.exams, admissionsLoading, admissionsError, admissionsPolicies]);

  const allocationHeaderPreview = useMemo(() => {
    if (!enabled.allocations || admissionsLoading || admissionsError) return null;
    const latest = oversubscriptionFromHistory(admissionsAllocationHistory)[0];
    if (!latest) return null;
    const factor = formatOversubscriptionFactor(latest.applications, latest.offers);
    if (!factor) return null;
    return (
      <span className="v2-drawer-chip v2-drawer-chip--allocation-factor">{factor}</span>
    );
  }, [enabled.allocations, admissionsLoading, admissionsError, admissionsAllocationHistory]);

  const { rows: gcseRows, loading: gcseLoading, error: gcseError } = useGcseResults(
    school?.id,
    enabled.gcse && Boolean(school?.id)
  );

  const { rows: grammarRankRows } = useGrammarBenchmarks(Boolean(school?.id));
  const gcseRankCard = useMemo(() => {
    if (!school?.id || !grammarRankRows?.length) return null;
    const ranked = withStageRanks(grammarRankRows);
    const mine = ranked.find((row) => Number(row.id) === Number(school.id));
    if (mine?.gcseRank == null) return null;
    const of = ranked.filter((row) => row.gcseRank != null).length;
    return { place: mine.gcseRank, of };
  }, [school?.id, grammarRankRows]);

  const gcseHeaderPreview = useMemo(() => {
    if (!enabled.gcse || gcseLoading || gcseError || gcseRows.length === 0) return null;
    const row = pickRowByYearTab(gcseRows, RESULTS_YEAR_TAB_ORDER);
    if (!row) return null;
    const pct = fmtResultsPctChip(row.gcse_entries_pct_grade_8_9);
    if (!pct) return null;
    const tier = resultsHeaderChipTier(row.gcse_entries_pct_grade_8_9, GCSE_CHIP_GRADES_89_HIGH_PCT);
    const tierClass =
      tier === "high"
        ? "v2-drawer-chip--results-header-high"
        : tier === "low"
          ? "v2-drawer-chip--results-header-low"
          : "";
    const gcse89n = Number(row.gcse_entries_pct_grade_8_9);
    const showGcseOutstandingStar =
      Number.isFinite(gcse89n) && gcse89n > GCSE_CHIP_GRADES_89_STAR_PCT;
    return (
      <span className={["v2-drawer-chip", tierClass].filter(Boolean).join(" ")}>
        Grades 8 or 9 : {pct}
        {showGcseOutstandingStar ? (
          <span className="v2-drawer-chip__results-outstanding-star" aria-hidden>
            ★
          </span>
        ) : null}
      </span>
    );
  }, [enabled.gcse, gcseLoading, gcseError, gcseRows]);

  const { rows: ks5Rows, loading: ks5Loading, error: ks5Error } = useKs5Results(
    school?.id,
    enabled.alevel && Boolean(school?.id)
  );

  const { rows: inspectionRows, loading: inspectionLoading, error: inspectionError } = useInspections(
    school?.id,
    enabled.inspection && Boolean(school?.id)
  );

  const inspectionHeaderPreview = useMemo(() => {
    if (!enabled.inspection || inspectionLoading || inspectionError || !inspectionRows?.length) return null;
    const latest = inspectionRows[0];
    const chip = getInspectionOverallChip(latest?.overall_grade);
    return (
      <span
        className={`v2-drawer-chip v2-drawer-chip--single-line v2-inspection-grade-chip v2-inspection-grade-chip--${chip.tier}`}
      >
        {chip.label}
      </span>
    );
  }, [enabled.inspection, inspectionLoading, inspectionError, inspectionRows]);

  const alevelHeaderPreview = useMemo(() => {
    if (!enabled.alevel || ks5Loading || ks5Error || ks5Rows.length === 0) return null;
    const row = pickRowByYearTab(ks5Rows, RESULTS_YEAR_TAB_ORDER);
    if (!row) return null;

    // IB / Academic fallback: GCE A*/A % is empty — show IB + DfE average grade instead of "—"
    if (isKs5AcademicIbFallback(row)) {
      const grade =
        row.aps_per_entry_grade != null && String(row.aps_per_entry_grade).trim() !== ""
          ? String(row.aps_per_entry_grade).trim()
          : null;
      const aps =
        row.aps_per_entry != null && Number.isFinite(Number(row.aps_per_entry))
          ? Number(row.aps_per_entry).toFixed(1)
          : null;
      const label = grade ? `IB · ${grade}` : aps != null ? `IB · ${aps}` : "IB";
      return (
        <span
          className="v2-drawer-chip v2-drawer-chip--results-header-ib"
        >
          {label}
        </span>
      );
    }

    const pct = fmtResultsPctChip(row.alevel_entries_pct_grade_astar_a);
    if (!pct) return null;
    const tier = resultsHeaderChipTier(row.alevel_entries_pct_grade_astar_a, ALEVEL_CHIP_ASTAR_A_HIGH_PCT);
    const tierClass =
      tier === "high"
        ? "v2-drawer-chip--results-header-high"
        : tier === "low"
          ? "v2-drawer-chip--results-header-low"
          : "";
    const astarAn = Number(row.alevel_entries_pct_grade_astar_a);
    const showAlevelOutstandingStar =
      Number.isFinite(astarAn) && astarAn > ALEVEL_CHIP_ASTAR_A_STAR_PCT;
    return (
      <span className={["v2-drawer-chip", tierClass].filter(Boolean).join(" ")}>
        A* or A : {pct}
        {showAlevelOutstandingStar ? (
          <span className="v2-drawer-chip__results-outstanding-star" aria-hidden>
            ★
          </span>
        ) : null}
      </span>
    );
  }, [enabled.alevel, ks5Loading, ks5Error, ks5Rows]);

  const { rows: subjectRows, loading: subjectsLoading, error: subjectsError } = useSchoolSubjects(
    school?.id,
    enabled.subjects && Boolean(school?.id)
  );

  const subjectsHeaderPreview = useMemo(() => {
    if (!enabled.subjects) return null;
    if (subjectsLoading || subjectsError) return null;
    const list = Array.isArray(subjectRows) ? subjectRows : [];
    const gcseCount = list.filter((r) => r.level !== "alevel").length;
    const alevelCount = list.filter((r) => r.level === "alevel").length;
    return (
      <>
        <span
          className="v2-drawer-chip v2-drawer-chip--single-line"
        >
          GCSE : {gcseCount}
        </span>
        <span
          className="v2-drawer-chip v2-drawer-chip--single-line"
        >
          A-Levels : {alevelCount}
        </span>
      </>
    );
  }, [enabled.subjects, subjectsLoading, subjectsError, subjectRows]);

  const destinationsHeaderPreview = useMemo(() => {
    if (!enabled.destinations || !school) return null;
    const hasSchoolOx =
      school.oxbridge_offers != null ||
      (school.source_url_oxbridge_offers != null &&
        String(school.source_url_oxbridge_offers).trim() !== "");
    if (!hasSchoolOx) return null;
    const n =
      school.oxbridge_offers != null && school.oxbridge_offers !== ""
        ? String(school.oxbridge_offers)
        : "—";
    return (
      <span
        className="v2-drawer-chip v2-drawer-chip--oxbridge v2-drawer-chip--single-line"
      >
        Oxbridge : {n}
      </span>
    );
  }, [enabled.destinations, school]);

  const [sectionCap, setSectionCap] = useState(null);
  const reportReference = useCallback((height) => {
    const next = Math.round(height * 1.1);
    setSectionCap((prev) => (prev === next ? prev : next));
  }, []);
  const sectionScrollProps = { scrollIntoViewOnExpand: true, scrollParentRef: drawerScrollRef };

  return (
    <aside className="v2-right-drawer" aria-label="School details drawer">
      <div className="v2-drawer-header">
        <div className="v2-drawer-title-wrap">
          {journeyAvailable && onOpenJourney ? (
            <button
              type="button"
              className="v2-drawer-title v2-drawer-title--link"
              onClick={() => onOpenJourney(school)}
              title="Open the school guide"
            >
              <span className="v2-drawer-title-text">
                {school.display_name || school.name}
              </span>
              <svg
                className="v2-drawer-title-arrow"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </button>
          ) : (
            <div className="v2-drawer-title">
              {school.display_name || school.name}
            </div>
          )}
          {school.local_authority ? (
            <div className="v2-drawer-subtitle">{school.local_authority}</div>
          ) : null}
        </div>

        <button
          type="button"
          className="v2-icon-btn"
          title="Close"
          onClick={() => onClose?.()}
        >
          ✕
        </button>
      </div>

      <SectionCapContext.Provider value={{ cap: sectionCap, reportReference }}>
      <div ref={drawerScrollRef} className="v2-drawer-scroll">
        {/* 1) School Details */}
        <Section
          {...sectionScrollProps}
          reference
          title="School Details"
          icon={DRAWER_ICONS.details}
          preview={
            (school.gender_type || school.school_type) ? (
              <>
                {school.gender_type && (
                  <span
                    className={`v2-drawer-chip v2-drawer-chip--gender v2-drawer-chip--${(
                      school.gender_type || ""
                    ).toLowerCase()}`}
                  >
                    {school.gender_type}
                  </span>
                )}
                {school.school_type && (
                  <span className="v2-drawer-chip">{school.school_type}</span>
                )}
              </>
            ) : null
          }
        >
          <div className="v2-school-details">
            <div className="ds-tiles">
              {onOpenRanking ? (
                <button
                  type="button"
                  className="ds-tile ds-pan ds-tile--link"
                  onClick={() => onOpenRanking()}
                  aria-label={
                    gcseRankCard
                      ? `GCSE Rank ${gcseRankCard.place}. Open ranking.`
                      : "Open GCSE ranking"
                  }
                >
                  <span className="ds-tile-label">
                    GCSE Rank
                    <InfoTip
                      label="About GCSE Rank"
                      text={
                        gcseRankCard
                          ? `Place by Attainment 8 among ${gcseRankCard.of} grammar schools. Click to open the ranking page.`
                          : "Place by Attainment 8 among grammar schools. Click to open the ranking page."
                      }
                    />
                  </span>
                  <strong>{gcseRankCard ? String(gcseRankCard.place) : "—"}</strong>
                </button>
              ) : (
                <div className="ds-tile ds-pan">
                  <span className="ds-tile-label">
                    GCSE Rank
                    <InfoTip
                      label="About GCSE Rank"
                      text={
                        gcseRankCard
                          ? `Place by Attainment 8 among ${gcseRankCard.of} grammar schools.`
                          : "Place by Attainment 8 among grammar schools."
                      }
                    />
                  </span>
                  <strong>{gcseRankCard ? String(gcseRankCard.place) : "—"}</strong>
                </div>
              )}
              {[
                ["Age range", school.age_range],
                ["Phase", school.phase],
                ["Local authority", school.local_authority || school.council_name],
                ["Day / boarding", school.boarding_type],
                ["Selective", school.selectivity_type],
                ["URN", school.school_code],
                ["Religious affiliation", school.religious_affiliation],
                ["Fees", school.fees],
              ].map(([label, value]) => (
                <div className={`ds-tile${String(value || "").length > 28 ? " ds-tile--wide" : ""}`} key={label}>
                  <span>{label}</span>
                  <strong>{value || "—"}</strong>
                </div>
              ))}
            </div>
            <div className="ds-contact">
              <div>
                <span>Website</span>
                <strong>
                  {school.website ? (
                    <a
                      className="v2-drawer-link"
                      href={school.website.startsWith("http") ? school.website : `https://${school.website}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {school.website.replace(/^https?:\/\//, "")}
                    </a>
                  ) : (
                    "—"
                  )}
                </strong>
              </div>
              <div>
                <span>Gov.uk</span>
                <strong>
                  {school.school_code ? (
                    <a
                      className="v2-drawer-link"
                      href={`https://get-information-schools.service.gov.uk/Establishments/Establishment/Details/${school.school_code}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View on Gov.uk →
                    </a>
                  ) : (
                    "—"
                  )}
                </strong>
              </div>
              <div>
                <span>Address</span>
                <strong>{school.address || "—"}</strong>
              </div>
              <div>
                <span>Phone</span>
                <strong>
                  {school.phone ? (
                    <a className="v2-drawer-link" href={`tel:${school.phone}`}>
                      {school.phone}
                    </a>
                  ) : (
                    "—"
                  )}
                </strong>
              </div>
            </div>
          </div>
        </Section>

        {/* 3) 11+ Catchment */}
        {enabled.catchment ? (
          <Section
            {...sectionScrollProps}
            title="11+ Catchment"
            icon={DRAWER_ICONS.catchment}
            preview={
              catchmentOpenSeatsChip ? (
                <span
                  className={`v2-drawer-chip v2-drawer-chip--open-seats v2-drawer-chip--open-seats-${catchmentOpenSeatsChip.tier}`}
                >
                  {catchmentOpenSeatsChip.label}
                </span>
              ) : null
            }
            onHeaderClick={(isOpen) => {
              const id = school?.id;
              if (!id || !onShowCatchment) return;
              const selected = selectedIds.includes(id);
              if (isOpen && !selected) onShowCatchment(id);
              if (!isOpen && selected) onShowCatchment(id);
            }}
          >
            <CatchmentPanel
              payload={payload}
              school={school}
              loading={loading}
              error={error}
              selectedIds={selectedIds}
              onShowCatchment={onShowCatchment}
            />
          </Section>
        ) : null}

        {/* 4) 11+ Exam Details */}
        {enabled.exams ? (
          <Section
            {...sectionScrollProps}
            title="11+ Exam Details"
            icon={DRAWER_ICONS.exam}
            preview={examHeaderPreview}
            previewClassName="v2-drawer-section-preview--exam-fit"
          >
            <ExamDetailsPanel
              policies={admissionsPolicies}
              loading={admissionsLoading}
              error={admissionsError}
            />
          </Section>
        ) : null}

        {/* 5) 11+ Allocation details */}
        {enabled.allocations ? (
          <Section
            {...sectionScrollProps}
            title="11+ Allocation details"
            icon={DRAWER_ICONS.allocation}
            preview={allocationHeaderPreview}
          >
            <AllocationPanel
              school={school}
              policies={admissionsPolicies}
              allocationHistory={admissionsAllocationHistory}
              loading={admissionsLoading}
              error={admissionsError}
            />
          </Section>
        ) : null}

        {/* 6) GCSE results */}
        {enabled.gcse ? (
          <Section {...sectionScrollProps} title="GCSE results" icon={DRAWER_ICONS.gcse} preview={gcseHeaderPreview}>
            <GcseResultsPanel schoolId={school?.id} compare />
          </Section>
        ) : null}

        {/* 7) A level results */}
        {enabled.alevel ? (
          <Section {...sectionScrollProps} title="A level results" icon={DRAWER_ICONS.alevel} preview={alevelHeaderPreview}>
            <AlevelResultsPanel schoolId={school?.id} compare />
          </Section>
        ) : null}

        {/* 8) Post-18 destinations (DfE) — panel title kept for parents searching “Oxbridge” */}
        {enabled.destinations ? (
          <Section
            {...sectionScrollProps}
            title="Oxbridge & Destinations"
            icon={DRAWER_ICONS.oxbridge}
            preview={destinationsHeaderPreview}
            previewClassName="v2-drawer-section-preview--nowrap"
          >
            <Destinations1618Panel
              schoolId={school?.id}
              school={school}
              graphic
            />
          </Section>
        ) : null}

        {/* 9) Inspections */}
        {enabled.inspection ? (
          <Section
            {...sectionScrollProps}
            title="Inspections"
            icon={DRAWER_ICONS.inspection}
            preview={inspectionHeaderPreview}
            previewClassName="v2-drawer-section-preview--nowrap"
          >
            <InspectionsPanel schoolId={school?.id} enabled={enabled.inspection} />
          </Section>
        ) : null}

        {/* 10) Subjects (KS4 + post-16 entries) */}
        {enabled.subjects ? (
          <Section
            {...sectionScrollProps}
            title="Subjects"
            icon={DRAWER_ICONS.subjects}
            preview={subjectsHeaderPreview}
            previewClassName="v2-drawer-section-preview--subjects-chips"
          >
            <SubjectsPanel schoolId={school?.id} enabled={enabled.subjects} />
          </Section>
        ) : null}

        {journeyAvailable && onOpenJourney ? (
          <div className="v2-drawer-section">
            <button
              type="button"
              className="v2-path-card"
              onClick={() => onOpenJourney(school)}
            >
              <span className="v2-path-card-copy">
                <span className="v2-path-card-title">
                  {`${(school.display_name || school.name || "School")
                    .replace(/^The\s+/i, "")
                    .replace(/\s+School$/i, "")} guide`}
                </span>
                <span className="v2-path-card-text">Admissions made clear</span>
              </span>
              <svg
                className="v2-path-card-arrow"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </button>
          </div>
        ) : null}
      </div>
      </SectionCapContext.Provider>
    </aside>
  );
}